import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../features/auth/context/AuthContext';
import { FaLock, FaCheck, FaExclamationTriangle, FaEye, FaEyeSlash } from 'react-icons/fa';
import Logo from '../components/common/Logo';
import Parse from '../config/parse';

const PasswordSetupPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: 'Ingresa una contraseña',
    color: 'text-muted'
  });

  const { redirectToAppropriateRoute } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token and userId from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const userIdParam = params.get('userId');

    if (tokenParam) setToken(tokenParam);
    if (userIdParam) setUserId(userIdParam);
  }, [location]);

  // Password strength checker
  const checkPasswordStrength = (password) => {
    if (!password) {
      return {
        score: 0,
        message: 'Ingresa una contraseña',
        color: 'text-muted'
      };
    }

    let score = 0;
    
    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    
    // Determine message and color based on score
    let message, color;
    
    if (score < 3) {
      message = 'Débil';
      color = 'text-danger';
    } else if (score < 5) {
      message = 'Moderada';
      color = 'text-warning';
    } else {
      message = 'Fuerte';
      color = 'text-success';
    }
    
    return { score, message, color };
  };

  // Update password strength when password changes
  useEffect(() => {
    setPasswordStrength(checkPasswordStrength(password));
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!password || !confirmPassword) {
      setError('Por favor ingresa y confirma tu contraseña');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordStrength.score < 3) {
      setError('Por favor usa una contraseña más segura');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // If we have a token and userId, this is a password reset flow
      if (token && userId) {
        // Verify the token is valid
        const response = await Parse.Cloud.run('verifyPasswordResetToken', {
          token,
          userId
        });
        
        if (!response.success) {
          throw new Error(response.message || 'Token inválido o expirado');
        }
        
        // Set the new password
        await Parse.Cloud.run('resetPassword', {
          token,
          userId,
          newPassword: password
        });
      } else {
        // This is a logged-in user changing their password
        const currentUser = await Parse.User.current();
        if (!currentUser) {
          throw new Error('Debes iniciar sesión para cambiar tu contraseña');
        }
        
        // Update the password
        currentUser.set('password', password);
        await currentUser.save(null, { useMasterKey: true });
      }
      
      // Show success message
      setSuccess(true);
      
      // Clear form
      setPassword('');
      setConfirmPassword('');
      
      // Redirect after a short delay if this was a password reset
      if (token && userId) {
        setTimeout(() => {
          redirectToAppropriateRoute();
        }, 3000);
      }
    } catch (error) {
      setError(error.message || 'Error al configurar la contraseña. Por favor intenta de nuevo.');
      console.error('Error de configuración de contraseña:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <div className="d-inline-block mb-3">
                  <Logo size="medium" showText={false} />
                </div>
                <h2 className="fw-bold">Configurar Contraseña</h2>
                <p className="text-muted">
                  {token && userId 
                    ? 'Crea una nueva contraseña para tu cuenta' 
                    : 'Actualiza la contraseña de tu cuenta'}
                </p>
              </div>
              
              {error && (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                  <FaExclamationTriangle className="me-2" />
                  <div>{error}</div>
                </div>
              )}
              
              {success && (
                <div className="alert alert-success d-flex align-items-center" role="alert">
                  <FaCheck className="me-2" />
                  <div>
                    {token && userId 
                      ? 'Contraseña configurada exitosamente. Serás redirigido en breve...' 
                      : 'Contraseña actualizada exitosamente.'}
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    Nueva Contraseña
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaLock />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      id="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading || success}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading || success}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <div className={`small mt-1 ${passwordStrength.color}`}>
                    Fortaleza: {passwordStrength.message}
                  </div>
                  <div className="small text-muted mt-1">
                    La contraseña debe tener al menos 8 caracteres e incluir letras mayúsculas, 
                    minúsculas, números y símbolos.
                  </div>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirmar Contraseña
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaLock />
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control"
                      id="confirmPassword"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading || success}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading || success}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {password && confirmPassword && (
                    <div className={`small mt-1 ${password === confirmPassword ? 'text-success' : 'text-danger'}`}>
                      {password === confirmPassword ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2 mb-3"
                  disabled={loading || success}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-2" />
                      {token && userId ? 'Configurar Contraseña' : 'Actualizar Contraseña'}
                    </>
                  )}
                </button>
                
                {!token && !userId && (
                  <div className="text-center">
                    <p className="mb-0">
                      <Link to="/dashboard" className="text-decoration-none">
                        Cancelar y volver al dashboard
                      </Link>
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
          
          <div className="text-center mt-4">
            <Link to="/" className="text-decoration-none">
              &larr; Volver a la página principal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordSetupPage;
